import { Link, Navigate, Route, Routes } from "react-router";

import { cn, focusRingClassName } from "@ceedero/ui";

import { AccountPage } from "@/account/components/account-page/account-page";
import { SignInPage } from "@/account/components/sign-in-page";
import { AccountMenu } from "@/account/components/account-menu";
import { useAccountProfile } from "@/account/hooks/use-account";
import { CeederoApiError } from "@/api/ceedero-api";
import { APP_SHELL_MEASURE_CLASS_NAMES } from "@/components/app-page";
import { BrandMark } from "@/components/brand-mark";
import { DevicesPage } from "@/devices/components/devices-page";
import { HomePage } from "@/home/components/home-page";
import { MusicCatalogPage } from "@/music-catalog/components/music-catalog-page/music-catalog-page";
import { ProjectDetailPage } from "@/music-catalog/components/project-detail-page/project-detail-page";
import { ProjectHistoryPage } from "@/project-history/components/project-history-page";
import { SettingsPage } from "@/settings/components/settings-page";
import { ShelfPage } from "@/shelf/components/shelf-page";
import { useTranslation } from "@/localization/hooks/use-translation";
import { AppNavigation } from "@/navigation/components/app-navigation";
import { ROUTES } from "@/navigation/constants/routes";
import { useScrollToTopOnNavigation } from "@/navigation/hooks/use-scroll-to-top-on-navigation";

/**
 * Which of the two halves of this application the visitor sees.
 *
 * Answered by asking the API who they are, because the session cookie is
 * `HttpOnly` and there is nothing local to consult. A `401` is the answer
 * "nobody", not a failure — so it draws the sign-in page rather than an error.
 *
 * The sign-in page is drawn for every path rather than only for `/`: a link to
 * a page of somebody's account is still a link somebody signed out can follow,
 * and the honest answer to it is to ask who they are.
 */
export const App = () => {
  const { t } = useTranslation();
  const profile = useAccountProfile();
  useScrollToTopOnNavigation();

  if (profile.isPending) {
    // Deliberately blank rather than a spinner: this resolves in one round trip
    // and a spinner that flashes for 200ms is noise, not feedback.
    return <div className="min-h-dvh bg-background" />;
  }

  const isSignedOut = profile.error instanceof CeederoApiError && profile.error.status === 401;
  if (isSignedOut || !profile.data) {
    return <SignInPage />;
  }

  return (
    <div className="flex min-h-dvh flex-col bg-background lg:h-dvh lg:min-h-0">
      <header className="shrink-0 border-b border-border bg-card">
        {/*
          One row on a wide window and two on a narrow one, from one set of
          elements rather than two copies: the pages wrap to their own line
          below the brand and the account, which a second rendered navigation
          would have achieved at the cost of two landmarks with one name.
        */}
        <div
          className={cn(
            APP_SHELL_MEASURE_CLASS_NAMES,
            "flex flex-wrap items-center gap-x-6 gap-y-1 py-2 sm:h-14 sm:flex-nowrap sm:py-0",
          )}
        >
          <Link
            to={ROUTES.home}
            aria-label={t("navigation.home")}
            className={cn("order-1 flex items-center gap-2.5 rounded-md", focusRingClassName)}
          >
            <BrandMark className="size-8 shrink-0 text-primary" />
            <span className="font-wordmark pt-0.5 text-2xl leading-none tracking-[0.02em]">
              {t("app.name")}
            </span>
          </Link>
          <AccountMenu className="order-2 ml-auto sm:order-3" />
          <AppNavigation className="order-3 w-full min-w-0 sm:order-2 sm:w-auto" />
        </div>
      </header>

      {/*
        Clipped from `lg`, where the page fills the window: each page rises a
        few pixels into place as it appears, and unclipped that movement pushed
        past the window's foot and flashed a scrollbar on every navigation.
      */}
      <div className="flex-1 lg:min-h-0 lg:overflow-hidden">
        <Routes>
          <Route path={ROUTES.home} element={<HomePage />} />
          <Route path={ROUTES.catalog} element={<MusicCatalogPage />} />
          <Route path={ROUTES.catalog + "/:desktopProjectId"} element={<ProjectDetailPage />} />
          <Route
            path={ROUTES.catalog + "/:desktopProjectId/versions"}
            element={<ProjectHistoryPage />}
          />
          <Route path={ROUTES.shelves} element={<ShelfPage />} />
          <Route path={ROUTES.devices} element={<DevicesPage />} />
          <Route path={ROUTES.account} element={<AccountPage />} />
          <Route path={ROUTES.settings} element={<SettingsPage />} />
          {/*
            Anything else is the home page rather than a "not found": every path
            this application has is in the navigation above, so an unknown one
            is a stale link or a typo, and a dead end helps nobody.
          */}
          <Route path="*" element={<Navigate to={ROUTES.home} replace />} />
        </Routes>
      </div>
    </div>
  );
};
